export type OptionItem = { value: string; label: string };

const COUNTRY_ALIASES: Record<string, string> = {
  usa: 'US',
  'u.s.': 'US',
  'u.s.a.': 'US',
  'united states of america': 'US',
  uk: 'GB',
  'u.k.': 'GB',
  'great britain': 'GB',
};

/** Match language by code (en) or label (English). */
export function findLanguageOption(
  options: OptionItem[],
  raw?: string | null
): OptionItem | null {
  if (!raw?.trim()) return null;
  const n = raw.trim().toLowerCase();
  return (
    options.find(
      (o) => o.value.toLowerCase() === n || o.label.toLowerCase() === n
    ) || null
  );
}

/** Match country by code (US), label (United States), or common aliases (USA). */
export function findCountryOption(
  options: OptionItem[],
  raw?: string | null
): OptionItem | null {
  if (!raw?.trim()) return null;
  const trimmed = raw.trim();
  const n = trimmed.toLowerCase();
  const upper = trimmed.toUpperCase();
  const alias = COUNTRY_ALIASES[n];
  return (
    options.find(
      (o) =>
        o.value === upper ||
        o.value.toLowerCase() === n ||
        o.label.toLowerCase() === n ||
        (alias != null && o.value === alias)
    ) || null
  );
}

/**
 * Resolve category value: enum code, exact label, or fuzzy label match
 * (e.g. "Technology" → TECHNOLOGY_GADGETS).
 */
export function resolveCategoryValue(
  options: OptionItem[],
  raw?: string | null
): string {
  if (!raw?.trim()) return '';
  const trimmed = raw.trim();
  const byValue = options.find((o) => o.value === trimmed);
  if (byValue) return byValue.value;

  const lower = trimmed.toLowerCase();
  const byLabel = options.find((o) => o.label.toLowerCase() === lower);
  if (byLabel) return byLabel.value;

  const fuzzy = options.find((o) => {
    const label = o.label.toLowerCase();
    return (
      label.startsWith(lower) ||
      label.includes(lower) ||
      lower.includes(label.split(/[&/]/)[0].trim())
    );
  });
  if (fuzzy) return fuzzy.value;

  return trimmed;
}

export function findCategoryOption(
  options: OptionItem[],
  raw?: string | null
): OptionItem | null {
  const resolved = resolveCategoryValue(options, raw);
  if (!resolved) return null;
  return options.find((o) => o.value === resolved) || { value: resolved, label: resolved };
}

/**
 * Autocomplete selected value: preferred matched option, otherwise keep raw text visible.
 */
export function toAutocompleteOption(
  options: OptionItem[],
  raw: string | undefined | null,
  kind: 'country' | 'language' | 'category'
): OptionItem | null {
  if (!raw?.trim()) return null;
  const found =
    kind === 'country'
      ? findCountryOption(options, raw)
      : kind === 'language'
        ? findLanguageOption(options, raw)
        : findCategoryOption(options, raw);
  if (found) return found;
  return { value: raw, label: raw };
}
